import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import ProjectForm from '../../components/ProjectForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  name: '', location: '', capacity: '', status: 'ongoing',
  client_name: '', completion_date: '', description: '', image_url: '', video_url: ''
};

const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/projects`);
      setProjects(data);
    } catch (error) {
      console.error('Projects fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      if (editingProject) {
        await axios.put(`${API_URL}/api/projects/${editingProject.id}`, formData, { withCredentials: true });
        setSuccess('Project updated successfully!');
      } else {
        await axios.post(`${API_URL}/api/projects`, formData, { withCredentials: true });
        setSuccess('Project created successfully!');
      }
      fetchProjects();
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(formatApiErrorDetail(error.response?.data?.detail) || 'Operation failed');
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name, location: project.location, capacity: project.capacity,
      status: project.status, client_name: project.client_name,
      completion_date: project.completion_date || '', description: project.description,
      image_url: project.image_url || '', video_url: project.video_url || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await axios.delete(`${API_URL}/api/projects/${id}`, { withCredentials: true });
      setSuccess('Project deleted successfully!');
      fetchProjects();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(formatApiErrorDetail(error.response?.data?.detail) || 'Delete failed');
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingProject(null);
    setShowModal(false);
    setError('');
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-700',
      ongoing: 'bg-blue-100 text-blue-700',
    };
    return styles[status.toLowerCase()] || 'bg-yellow-100 text-yellow-700';
  };

  return (
    <div data-testid="admin-projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-primary mb-2" data-testid="projects-title">Projects Management</h1>
          <p className="text-text_muted">Manage your solar power plant projects</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center space-x-2 px-6 py-3 bg-accent text-white rounded font-semibold hover:bg-opacity-90 transition-all" data-testid="add-project-button">
          <Plus size={20} />
          <span>Add Project</span>
        </button>
      </div>

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded text-green-700" data-testid="success-message">{success}</div>
      )}

      {loading ? (
        <div className="text-center py-20" data-testid="loading">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text_muted">Loading projects...</p>
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-border overflow-hidden">
          <table className="w-full" data-testid="projects-table">
            <thead className="bg-surface border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Name</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Client</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Location</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Capacity</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project, index) => (
                <tr key={project.id} className="border-b border-border last:border-b-0" data-testid={`project-row-${index}`}>
                  <td className="px-6 py-4 text-text_main">{project.name}</td>
                  <td className="px-6 py-4 text-text_muted">{project.client_name}</td>
                  <td className="px-6 py-4 text-text_muted">{project.location}</td>
                  <td className="px-6 py-4 text-text_muted">{project.capacity}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded text-xs font-semibold ${getStatusBadge(project.status)}`}>{project.status}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button onClick={() => handleEdit(project)} className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors" data-testid={`edit-project-${index}`}>
                        <Edit size={18} />
                      </button>
                      <button onClick={() => handleDelete(project.id)} className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors" data-testid={`delete-project-${index}`}>
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <ProjectForm
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleSubmit}
          onCancel={resetForm}
          isEditing={!!editingProject}
          error={error}
        />
      )}
    </div>
  );
};

export default AdminProjects;
